import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import { IEcommerceQuestionSchema } from '../../types.ts'
import { formatPriceWithLocale } from '../../utils/formatPriceWithLocale.ts'
import style from './Header.module.scss'
import tescoLogo from '../assets/icons/tescoLogo.svg'
import searchIcon from '../assets/icons/search.svg'
import basketIcon from '../assets/icons/basketWhite.svg'
import searchBlueIcon from '../assets/icons/searchBlue.svg'
import shareIcon from '../assets/icons/share.svg'

interface IHeaderProps {
  schema: IEcommerceQuestionSchema
  totalPrice: number
  findability: boolean
  clearSelection: () => void
  onCartOpen: () => void
  onHomeClick: () => void
  showCart: boolean
  productOpen: boolean
  // Shown once the product page's own title has scrolled under the bar.
  productTitle: string | null
  onItemClose: () => void
}

const UTILITY_LINKS = [
  'utilityInsurance',
  'utilityBank',
  'utilityMobile',
  'utilityDeliverySaver',
  'utilityStoreLocator',
  'utilityHelp',
  'utilityFeedback'
]

const NAV_ITEMS = [
  { key: 'navAllDepartments', chevron: true },
  { key: 'navGroceries' },
  { key: 'navFavourites' },
  { key: 'navSpecialOffers' },
  { key: 'navClubcard' },
  { key: 'navClothing' },
  { key: 'navMarketplace' },
  { key: 'navNewTrending' },
  { key: 'navRecipes' }
]

const SUB_NAV_ITEMS = [
  { key: 'subNavClothing' },
  { key: 'subNavBestNightIn' },
  { key: 'subNavMarketplace' },
  { key: 'subNavFreshFood' },
  { key: 'subNavBakery' },
  { key: 'subNavFrozenFood' },
  { key: 'subNavTreatsSnacks' },
  { key: 'subNavFoodCupboard' },
  { key: 'subNavDrinks' },
  { key: 'subNavBabyToddler' },
  { key: 'subNavHealthBeauty' },
  { key: 'subNavPets' },
  { key: 'subNavMore', chevron: true }
]

const Header = ({
  schema,
  totalPrice,
  findability,
  clearSelection,
  onCartOpen,
  onHomeClick,
  showCart,
  productOpen,
  productTitle,
  onItemClose
}: IHeaderProps) => {
  const { t, languageTag } = useSurveyLocale()
  const viewport = useViewport()

  const searchTerm = schema.search_condition

  const logo = (
    <img src={tescoLogo} alt={t('questions', 'ec', 'tesco', 'store')} className={style.logo} onClick={onHomeClick} />
  )

  const search = (
    <div className={style.search} onClick={clearSelection} data-testid="SW-EC-Search">
      <div className={style.searchField}>
        <span
          className={`${style.searchText} ${searchTerm ? '' : style.searchPlaceholder}`}
          dangerouslySetInnerHTML={{ __html: searchTerm || t('questions', 'ec', 'tesco', 'search') }}
        />
        {searchTerm && (
          <i className={`pi pi-times ${style.searchClear}`} aria-label={t('questions', 'ec', 'tesco', 'clearSearch')} />
        )}
      </div>
      <span className={style.searchButton} aria-label={t('questions', 'ec', 'tesco', 'search')}>
        <img src={searchIcon} alt="" role="presentation" className={style.searchIcon} />
      </span>
    </div>
  )

  const basketInner = (
    <>
      <span className={style.basketBadge}>
        <img src={basketIcon} alt="" role="presentation" className={style.basketIcon} />
      </span>
      <span className={style.basketText}>
        <span
          className={style.basketTotal}
          dangerouslySetInnerHTML={{
            __html: formatPriceWithLocale(totalPrice, schema.price_mask, languageTag, true, false)
          }}
        />
        <span className={style.basketLabel}>{t('questions', 'ec', 'tesco', 'totalEstimated')}</span>
      </span>
    </>
  )

  if (viewport.mobile) {
    // One app bar, three faces: the basket title, the product's back + share, the results' search.
    let row: JSX.Element
    if (showCart) {
      row = (
        <>
          <span className={style.mobileTitle}>{t('questions', 'ec', 'tesco', 'cart')}</span>
          <div className={style.mobileOptionsSlot}>
            <EmptyLink color="#00539f">
              <span className={style.mobileOptions}>{t('questions', 'ec', 'tesco', 'options')}</span>
            </EmptyLink>
          </div>
        </>
      )
    } else if (productOpen) {
      row = (
        <>
          <button
            type="button"
            className={style.mobileBack}
            onClick={onItemClose}
            aria-label={t('questions', 'ec', 'tesco', 'back')}
          >
            <i className="pi pi-chevron-left" />
          </button>
          <span className={style.mobileProductTitle} dangerouslySetInnerHTML={{ __html: productTitle ?? '' }} />
          <EmptyLink color="#00539f">
            <img src={shareIcon} alt={t('questions', 'ec', 'tesco', 'share')} className={style.mobileShare} />
          </EmptyLink>
        </>
      )
    } else {
      row = (
        <>
          <EmptyLink color="#00539f">
            <span className={style.mobileBack} aria-label={t('questions', 'ec', 'tesco', 'back')}>
              <i className="pi pi-chevron-left" />
            </span>
          </EmptyLink>
          <div className={style.mobileSearchSlot}>
            <EmptyLink color="#00539f">
              <span className={style.mobileSearch} data-testid="SW-EC-Search">
                <img src={searchBlueIcon} alt="" role="presentation" className={style.mobileSearchIcon} />
                <span
                  className={`${style.mobileSearchText} ${searchTerm ? '' : style.searchPlaceholder}`}
                  dangerouslySetInnerHTML={{ __html: searchTerm || t('questions', 'ec', 'tesco', 'search') }}
                />
                {searchTerm && (
                  <i
                    className={`pi pi-times ${style.mobileSearchClear}`}
                    aria-label={t('questions', 'ec', 'tesco', 'clearSearch')}
                  />
                )}
              </span>
            </EmptyLink>
          </div>
        </>
      )
    }

    return (
      <header className={`${style.headerMobile} ${showCart ? style.headerMobileCart : ''}`} data-testid="SW-EC-Header">
        {row}
      </header>
    )
  }

  return (
    <header className={style.header} data-testid="SW-EC-Header">
      <div className={style.utilityBar}>
        <div className={`${style.inner} ${style.utilityInner}`}>
          {UTILITY_LINKS.map(key => (
            <EmptyLink key={key} color="#00539f">
              <span className={style.utilityLink}>{t('questions', 'ec', 'tesco', key)}</span>
            </EmptyLink>
          ))}
        </div>
      </div>
      <div className={style.mainBar}>
        <div className={`${style.inner} ${style.mainInner}`}>
          {logo}
          {search}
          {findability ? (
            <div className={style.basketSlot}>
              <EmptyLink color="#00539f">
                <span className={style.basket}>{basketInner}</span>
              </EmptyLink>
            </div>
          ) : (
            <button
              type="button"
              className={`${style.basketSlot} ${style.basket}`}
              onClick={onCartOpen}
              aria-label={t('questions', 'ec', 'tesco', 'basket')}
            >
              {basketInner}
            </button>
          )}
        </div>
      </div>
      <nav className={style.nav}>
        <div className={`${style.inner} ${style.navInner}`}>
          {NAV_ITEMS.map(({ key, chevron }) => (
            <EmptyLink key={key} color="#00539f">
              <span className={style.navItem}>
                {t('questions', 'ec', 'tesco', key)}
                {chevron && <i className={`pi pi-chevron-down ${style.chevron}`} />}
              </span>
            </EmptyLink>
          ))}
        </div>
      </nav>
      <div className={style.subNav}>
        <div className={`${style.inner} ${style.subNavInner}`}>
          {SUB_NAV_ITEMS.map(({ key, chevron }) => (
            <EmptyLink key={key} color="#00539f">
              <span className={style.subNavItem}>
                {t('questions', 'ec', 'tesco', key)}
                {chevron && <i className={`pi pi-chevron-down ${style.chevron}`} />}
              </span>
            </EmptyLink>
          ))}
        </div>
      </div>
    </header>
  )
}

export default Header
